import { signal } from "@preact/signals-core";
import { msg } from "@lit/localize";
import {
  deleteAppsByIdEnvironmentsByEnvId,
  deleteAppsByIdReleasesByReleaseId,
  getAppsByIdDeployments,
  getAppsByIdDeploymentsByDeploymentId,
  getAppsByIdEnvironments,
  getAppsByIdMyAccess,
  getAppsByIdReleases,
  getAppsByIdReleasesByReleaseId,
  getAppsByIdReleasesByReleaseIdCandidates,
  getAppsByIdReleasesByReleaseIdTasks,
  getProjectsByIdReleases,
  getProjectsByIdTasksByTaskIdDeployments,
  patchAppsByIdDeploymentsByDeploymentId,
  postAppsByIdDeployments,
  postAppsByIdDeploymentsByDeploymentIdApprove,
  postAppsByIdDeploymentsByDeploymentIdCancel,
  postAppsByIdDeploymentsByDeploymentIdDeploy,
  postAppsByIdDeploymentsByDeploymentIdReject,
  postAppsByIdDeploymentsByDeploymentIdResubmit,
  postAppsByIdDeploymentsByDeploymentIdRollback,
  postAppsByIdEnvironments,
  postAppsByIdReleases,
  postAppsByIdReleasesByReleaseIdTasks,
  postAppsByIdReleasesByReleaseIdTasksRemove,
  putAppsByIdEnvironmentsByEnvId,
  putAppsByIdEnvironmentsByEnvIdApprovers,
  putAppsByIdReleasesByReleaseId,
  putProjectsByIdTasksByTaskIdRelease,
} from "@/api";
import type {
  DtoAppAccessResponse,
  DtoAppResponse,
  DtoCreateDeploymentRequest,
  DtoCreateReleaseRequest,
  DtoEnvironmentRequest,
  DtoEnvironmentResponse,
  DtoReleaseResponse,
  DtoUpdateDeploymentRequest,
  DtoUpdateReleaseRequest,
} from "@/api";
import { showToast } from "@/components/ui/toast-store";
import { AppPermission } from "@/lib/permissions";
import { apps, fetchApps } from "@/store/apps";

/**
 * Release management store: the current app's access, environments and
 * releases (shared by the app tabs and the release/deployment pages), plus
 * thin API wrappers that throw an Error carrying the server's message.
 */

/** The server's message from a thrown SDK error ({ error: { message } }). */
export function errMsg(err: unknown, fallback: string): string {
  const m = (err as { error?: { message?: unknown } } | null)?.error?.message;
  if (typeof m === "string" && m) return m;
  return err instanceof Error && err.message ? err.message : fallback;
}

async function call<T>(p: Promise<{ data?: T }>, fallback: string): Promise<T> {
  try {
    const { data } = await p;
    return data as T;
  } catch (err) {
    throw new Error(errMsg(err, fallback));
  }
}

export const releaseHref = (appSlug: string, releaseId: string) =>
  `/apps/${encodeURIComponent(appSlug)}/releases/${
    encodeURIComponent(releaseId)
  }`;
export const deploymentHref = (appSlug: string, deploymentId: string) =>
  `/apps/${encodeURIComponent(appSlug)}/deployments/${
    encodeURIComponent(deploymentId)
  }`;
export const taskHref = (projectSlug: string, taskId: string) =>
  `/projects/${encodeURIComponent(projectSlug)}?task=${
    encodeURIComponent(taskId)
  }`;

export interface AppContextState {
  appId: string;
  access: DtoAppAccessResponse | null;
  environments: DtoEnvironmentResponse[];
  releases: DtoReleaseResponse[];
  isLoading: boolean;
}

export const appContext = signal<AppContextState>({
  appId: "",
  access: null,
  environments: [],
  releases: [],
  isLoading: false,
});

/**
 * "Latest request wins" guard: each call to the returned function starts a
 * request and returns `isCurrent()`, which turns false once a newer request
 * has started. Use it to drop responses that arrive out of order.
 */
export function latestOnly(): () => () => boolean {
  let seq = 0;
  return () => {
    const mine = ++seq;
    return () => mine === seq;
  };
}

const nextAppContextLoad = latestOnly();

/**
 * Loads access, environments and releases for an app (cached per app).
 * Switching apps clears the previous app's data first, so a failed load
 * never leaves one app's data attached to another (and is retried on the
 * next call); only the newest load may write.
 */
export async function loadAppContext(
  appId: string,
  force = false,
): Promise<void> {
  if (!appId) return;
  const cur = appContext.value;
  if (!force && cur.appId === appId && (cur.isLoading || cur.access)) return;
  const isCurrent = nextAppContextLoad();
  appContext.value = cur.appId === appId
    ? { ...cur, isLoading: true }
    : { appId, access: null, environments: [], releases: [], isLoading: true };
  try {
    const [access, envs, rels] = await Promise.all([
      getAppsByIdMyAccess({ path: { id: appId }, throwOnError: true }),
      getAppsByIdEnvironments({ path: { id: appId }, throwOnError: true }),
      getAppsByIdReleases({ path: { id: appId }, throwOnError: true }),
    ]);
    if (!isCurrent()) return;
    appContext.value = {
      appId,
      access: access.data ?? null,
      environments: [...(envs.data ?? [])].sort((a, b) =>
        (a.position ?? 0) - (b.position ?? 0)
      ),
      releases: rels.data ?? [],
      isLoading: false,
    };
  } catch (err) {
    if (!isCurrent()) return;
    appContext.value = { ...appContext.value, isLoading: false };
    showToast(errMsg(err, msg("Failed to load releases")), {
      variant: "error",
    });
  }
}

export function refreshAppContext(): Promise<void> {
  return loadAppContext(appContext.value.appId, true);
}

/** Whether the caller holds an app-level permission (from my-access). */
export function can(
  perm: (typeof AppPermission)[keyof typeof AppPermission],
): boolean {
  return appContext.value.access?.permissions?.includes(perm) ?? false;
}

export function isApproverFor(environmentId: string | undefined): boolean {
  return !!environmentId &&
    (appContext.value.access?.approver_environment_ids ?? []).includes(
      environmentId,
    );
}

export const releasesApi = {
  get: (appId: string, releaseId: string) =>
    call(
      getAppsByIdReleasesByReleaseId({
        path: { id: appId, releaseId },
        throwOnError: true,
      }),
      msg("Failed to load release"),
    ),
  create: (appId: string, body: DtoCreateReleaseRequest) =>
    call(
      postAppsByIdReleases({ path: { id: appId }, body, throwOnError: true }),
      msg("Failed to create release"),
    ),
  update: (appId: string, releaseId: string, body: DtoUpdateReleaseRequest) =>
    call(
      putAppsByIdReleasesByReleaseId({
        path: { id: appId, releaseId },
        body,
        throwOnError: true,
      }),
      msg("Failed to update release"),
    ),
  remove: (appId: string, releaseId: string) =>
    call(
      deleteAppsByIdReleasesByReleaseId({
        path: { id: appId, releaseId },
        throwOnError: true,
      }),
      msg("Failed to delete release"),
    ),
  tasks: (appId: string, releaseId: string) =>
    call(
      getAppsByIdReleasesByReleaseIdTasks({
        path: { id: appId, releaseId },
        throwOnError: true,
      }),
      msg("Failed to load release tasks"),
    ),
  candidates: (appId: string, releaseId: string, q: string) =>
    call(
      getAppsByIdReleasesByReleaseIdCandidates({
        path: { id: appId, releaseId },
        query: { q },
        throwOnError: true,
      }),
      msg("Failed to search tasks"),
    ),
  addTasks: (appId: string, releaseId: string, taskIds: string[]) =>
    call(
      postAppsByIdReleasesByReleaseIdTasks({
        path: { id: appId, releaseId },
        body: { task_ids: taskIds },
        throwOnError: true,
      }),
      msg("Failed to add tasks"),
    ),
  removeTasks: (appId: string, releaseId: string, taskIds: string[]) =>
    call(
      postAppsByIdReleasesByReleaseIdTasksRemove({
        path: { id: appId, releaseId },
        body: { task_ids: taskIds },
        throwOnError: true,
      }),
      msg("Failed to remove tasks"),
    ),
  forProject: (projectId: string, openOnly: boolean) =>
    call(
      getProjectsByIdReleases({
        path: { id: projectId },
        query: { open: openOnly },
        throwOnError: true,
      }),
      msg("Failed to load releases"),
    ),
  /** Links a task to a release; null unlinks it. */
  setTaskRelease: (
    projectId: string,
    taskId: string,
    releaseId: string | null,
  ) =>
    call(
      putProjectsByIdTasksByTaskIdRelease({
        path: { id: projectId, taskId },
        body: releaseId ? { release_id: releaseId } : {},
        throwOnError: true,
      }),
      msg("Failed to change the task's release"),
    ),
  taskDeployments: (projectId: string, taskId: string) =>
    call(
      getProjectsByIdTasksByTaskIdDeployments({
        path: { id: projectId, taskId },
        throwOnError: true,
      }),
      msg("Failed to load deployments"),
    ),
};

export const environmentsApi = {
  create: (appId: string, body: DtoEnvironmentRequest) =>
    call(
      postAppsByIdEnvironments({
        path: { id: appId },
        body,
        throwOnError: true,
      }),
      msg("Failed to add environment"),
    ),
  update: (appId: string, envId: string, body: DtoEnvironmentRequest) =>
    call(
      putAppsByIdEnvironmentsByEnvId({
        path: { id: appId, envId },
        body,
        throwOnError: true,
      }),
      msg("Failed to update environment"),
    ),
  remove: (appId: string, envId: string) =>
    call(
      deleteAppsByIdEnvironmentsByEnvId({
        path: { id: appId, envId },
        throwOnError: true,
      }),
      msg("Failed to delete environment"),
    ),
  setApprovers: (appId: string, envId: string, userIds: string[]) =>
    call(
      putAppsByIdEnvironmentsByEnvIdApprovers({
        path: { id: appId, envId },
        body: { user_ids: userIds },
        throwOnError: true,
      }),
      msg("Failed to update approvers"),
    ),
};

export const deploymentsApi = {
  list: (
    appId: string,
    query: {
      release_id?: string;
      environment_id?: string;
      status?: string;
      cursor?: string;
      limit?: number;
    } = {},
  ) =>
    call(
      getAppsByIdDeployments({
        path: { id: appId },
        query,
        throwOnError: true,
      }),
      msg("Failed to load deployments"),
    ),
  get: (appId: string, deploymentId: string) =>
    call(
      getAppsByIdDeploymentsByDeploymentId({
        path: { id: appId, deploymentId },
        throwOnError: true,
      }),
      msg("Failed to load deployment"),
    ),
  create: (appId: string, body: DtoCreateDeploymentRequest) =>
    call(
      postAppsByIdDeployments({
        path: { id: appId },
        body,
        throwOnError: true,
      }),
      msg("Failed to request deployment"),
    ),
  update: (
    appId: string,
    deploymentId: string,
    body: DtoUpdateDeploymentRequest,
  ) =>
    call(
      patchAppsByIdDeploymentsByDeploymentId({
        path: { id: appId, deploymentId },
        body,
        throwOnError: true,
      }),
      msg("Failed to update request"),
    ),
  approve: (appId: string, deploymentId: string, revision: number) =>
    call(
      postAppsByIdDeploymentsByDeploymentIdApprove({
        path: { id: appId, deploymentId },
        body: { expected_revision: revision },
        throwOnError: true,
      }),
      msg("Failed to approve"),
    ),
  reject: (
    appId: string,
    deploymentId: string,
    revision: number,
    reason: string,
  ) =>
    call(
      postAppsByIdDeploymentsByDeploymentIdReject({
        path: { id: appId, deploymentId },
        body: { expected_revision: revision, reason },
        throwOnError: true,
      }),
      msg("Failed to reject"),
    ),
  resubmit: (appId: string, deploymentId: string) =>
    call(
      postAppsByIdDeploymentsByDeploymentIdResubmit({
        path: { id: appId, deploymentId },
        throwOnError: true,
      }),
      msg("Failed to resubmit"),
    ),
  cancel: (appId: string, deploymentId: string, reason: string) =>
    call(
      postAppsByIdDeploymentsByDeploymentIdCancel({
        path: { id: appId, deploymentId },
        body: { reason },
        throwOnError: true,
      }),
      msg("Failed to cancel"),
    ),
  deploy: (appId: string, deploymentId: string) =>
    call(
      postAppsByIdDeploymentsByDeploymentIdDeploy({
        path: { id: appId, deploymentId },
        throwOnError: true,
      }),
      msg("Failed to mark as deployed"),
    ),
  rollback: (appId: string, deploymentId: string, reason: string) =>
    call(
      postAppsByIdDeploymentsByDeploymentIdRollback({
        path: { id: appId, deploymentId },
        body: { reason },
        throwOnError: true,
      }),
      msg("Failed to roll back"),
    ),
};

export type Tone = "neutral" | "info" | "success" | "warning" | "danger";

export function statusTone(
  kind: "deployment" | "release",
  status: string | undefined,
): Tone {
  if (kind === "release") {
    return status === "released"
      ? "success"
      : status === "in_progress"
      ? "info"
      : "neutral";
  }
  switch (status) {
    case "pending_approval":
      return "warning";
    case "approved":
      return "info";
    case "deployed":
      return "success";
    case "rejected":
    case "rolled_back":
      return "danger";
    default:
      return "neutral";
  }
}

export function statusLabel(
  kind: "deployment" | "release",
  status: string | undefined,
): string {
  switch (status) {
    case "planning":
      return msg("Planning");
    case "in_progress":
      return msg("In progress");
    case "released":
      return msg("Released");
    case "archived":
      return msg("Archived");
    case "pending_approval":
      return msg("Pending approval");
    case "approved":
      return msg("Approved");
    case "rejected":
      return msg("Rejected");
    case "deployed":
      return msg("Deployed");
    case "rolled_back":
      return msg("Rolled back");
    case "cancelled":
      return msg("Cancelled");
    default:
      return kind === "deployment" ? msg("Not requested") : (status ?? "");
  }
}

export function eventLabel(action: string | undefined): string {
  switch (action) {
    case "requested":
      return msg("requested the deployment");
    case "auto_approved":
      return msg("Approved automatically");
    case "updated":
      return msg("edited the request");
    case "approved":
      return msg("approved");
    case "rejected":
      return msg("rejected");
    case "resubmitted":
      return msg("resubmitted for approval");
    case "cancelled":
      return msg("cancelled");
    case "deployed":
      return msg("marked as deployed");
    case "rolled_back":
      return msg("rolled back");
    default:
      return action ?? "";
  }
}

// ---------------------------------------------------------------------------
// Workspace: every visible app's release data, for the Release management
// area and the sidebar warning.
// ---------------------------------------------------------------------------

export interface AppReleaseData {
  app: DtoAppResponse;
  access: DtoAppAccessResponse | null;
  environments: DtoEnvironmentResponse[];
  releases: DtoReleaseResponse[];
}

export interface RmWorkspaceState {
  items: AppReleaseData[];
  isLoading: boolean;
  loaded: boolean;
}

export const rmWorkspace = signal<RmWorkspaceState>({
  items: [],
  isLoading: false,
  loaded: false,
});

// Bumped by resetRmWorkspace so a load started for the previous workspace
// cannot write its (now foreign) apps back.
let workspaceGeneration = 0;

/** Forget every app's data, e.g. after switching workspace. */
export function resetRmWorkspace(): void {
  workspaceGeneration++;
  rmWorkspace.value = { items: [], isLoading: false, loaded: false };
}

let workspaceLoad: Promise<void> | null = null;
let workspaceStale = false;

/**
 * Loads access, environments and releases for every visible, non-archived
 * app. Calls made while a load is running trigger one more pass so the
 * result is never older than the last request.
 * ponytail: 3 requests per app; add an org-wide overview endpoint if the
 * number of apps makes this slow.
 */
export function loadRmWorkspace(): Promise<void> {
  if (workspaceLoad) {
    workspaceStale = true;
    return workspaceLoad;
  }
  workspaceLoad = (async () => {
    do {
      workspaceStale = false;
      await loadWorkspaceOnce();
    } while (workspaceStale);
  })().finally(() => {
    workspaceLoad = null;
  });
  return workspaceLoad;
}

async function loadWorkspaceOnce(): Promise<void> {
  const generation = workspaceGeneration;
  rmWorkspace.value = { ...rmWorkspace.value, isLoading: true };
  if (apps.value.apps.length === 0) await fetchApps();
  const list = apps.value.apps.filter((a) => a.id && !a.is_archived);
  const items = await Promise.all(list.map(async (app) => {
    const id = app.id ?? "";
    try {
      const [access, envs, rels] = await Promise.all([
        getAppsByIdMyAccess({ path: { id }, throwOnError: true }),
        getAppsByIdEnvironments({ path: { id }, throwOnError: true }),
        getAppsByIdReleases({ path: { id }, throwOnError: true }),
      ]);
      return {
        app,
        access: access.data ?? null,
        environments: [...(envs.data ?? [])].sort((a, b) =>
          (a.position ?? 0) - (b.position ?? 0)
        ),
        releases: rels.data ?? [],
      };
    } catch {
      return { app, access: null, environments: [], releases: [] };
    }
  }));
  if (generation !== workspaceGeneration) return;
  rmWorkspace.value = { items, isLoading: false, loaded: true };
}

type AppPermissionKey = (typeof AppPermission)[keyof typeof AppPermission];

export function canIn(
  d: AppReleaseData | undefined,
  perm: AppPermissionKey,
): boolean {
  return d?.access?.permissions?.includes(perm) ?? false;
}

/** Whether the caller is a listed approver of the environment. */
export function approvesIn(
  d: AppReleaseData | undefined,
  environmentId: string | undefined,
): boolean {
  return !!environmentId &&
    (d?.access?.approver_environment_ids ?? []).includes(environmentId);
}

/** Active approvers of an environment, excluding `requesterId`. */
export function eligibleApprovers(
  env: DtoEnvironmentResponse,
  requesterId?: string,
) {
  return (env.approvers ?? []).filter((a) =>
    a.is_active && a.user_id !== requesterId
  );
}

/** Approval-gated environments that nobody can approve. */
export function envsMissingApprovers(
  envs: DtoEnvironmentResponse[],
): DtoEnvironmentResponse[] {
  return envs.filter((e) =>
    e.requires_approval && !(e.approvers ?? []).some((a) => a.is_active)
  );
}

/** Apps the caller manages that have environments nobody can approve. */
export function setupWarningCount(items: AppReleaseData[]): number {
  return items.filter((d) =>
    canIn(d, AppPermission.ReleaseManage) &&
    envsMissingApprovers(d.environments).length > 0
  ).length;
}

export type SetupStepId =
  | "projects"
  | "approvers"
  | "release"
  | "tasks"
  | "deployment";

export interface SetupStep {
  id: SetupStepId;
  label: string;
  done: boolean;
}

/** The getting-started checklist for one app, in order. */
export function setupSteps(d: AppReleaseData): SetupStep[] {
  return [
    {
      id: "projects",
      label: msg("Add a project to the app"),
      done: (d.app.projects ?? []).some((p) => !p.is_archived),
    },
    {
      id: "approvers",
      label: msg("Assign approvers to environments that need approval"),
      done: d.environments.length > 0 &&
        envsMissingApprovers(d.environments).length === 0,
    },
    {
      id: "release",
      label: msg("Create the first release"),
      done: d.releases.length > 0,
    },
    {
      id: "tasks",
      label: msg("Add tasks to a release"),
      done: d.releases.some((r) => (r.progress?.total ?? 0) > 0),
    },
    {
      id: "deployment",
      label: msg("Request the first deployment"),
      done: d.releases.some((r) =>
        (r.environments ?? []).some((e) => !!e.deployment_id)
      ),
    },
  ];
}

/**
 * Next version after the app's latest release (`existing`, newest first):
 * bumps the last number (1.4.0 → 1.4.1, v2 → v3) until it is unused.
 * "1.0.0" for the first release; "" when the latest has no number.
 */
export function suggestNextVersion(existing: string[]): string {
  const versions = existing.filter(Boolean);
  if (versions.length === 0) return "1.0.0";
  const m = versions[0].match(/^(.*?)(\d+)(\D*)$/);
  if (!m) return "";
  let n = Number(m[2]);
  let v = "";
  do {
    n += 1;
    v = `${m[1]}${n}${m[3]}`;
  } while (versions.includes(v));
  return v;
}

/**
 * What to show for a release in one environment. The latest request there
 * can be cancelled or rolled back while an earlier deployment of the same
 * release is still live; the release then counts as deployed.
 */
export function effectiveEnvStatus(
  st: { status?: string; deployed?: boolean } | undefined,
): string {
  const s = st?.status ?? "";
  return st?.deployed && (s === "" || s === "cancelled" || s === "rolled_back")
    ? "deployed"
    : s;
}

export type CellAction =
  | "request"
  | "blocked-approver"
  | "after-previous"
  | "review"
  | "waiting"
  | "deploy"
  | "ready"
  | "live"
  | "superseded"
  | "open"
  | "none";

/**
 * What a release × environment cell of the pipeline board offers, given the
 * latest deployment status there. The server re-checks every rule.
 */
export function cellAction(o: {
  status: string;
  canRequest: boolean;
  canExecute: boolean;
  isApprover: boolean;
  previousDeployed: boolean;
  hasEligibleApprover: boolean;
  isLive: boolean;
}): CellAction {
  switch (o.status) {
    case "pending_approval":
      return o.isApprover ? "review" : "waiting";
    case "approved":
      return o.canExecute ? "deploy" : "ready";
    case "deployed":
      return o.isLive ? "live" : "superseded";
    case "rejected":
      return "open";
  }
  // Never requested, cancelled or rolled back: a new request is possible.
  if (!o.canRequest) return "none";
  if (!o.previousDeployed) return "after-previous";
  if (!o.hasEligibleApprover) return "blocked-approver";
  return "request";
}

export function formatDate(value: string | undefined): string {
  if (!value) return "";
  // Date-only values (YYYY-MM-DD) are calendar dates: don't shift by timezone.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

export function formatDateTime(value: string | undefined): string {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}
